import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import WorkCard from "@/components/feature/WorkCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterBar, { type FilterState } from "@/pages/category/components/FilterBar";
import Pagination from "@/pages/category/components/Pagination";
import { works } from "@/mocks/works";
import {
  categoryMeta,
  genreOptions,
  regionOptions,
  yearOptions,
} from "@/mocks/filters";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type } = useParams();
  const validType = type === "movie" || type === "tv" ? type : null;

  const [filters, setFilters] = useState<FilterState>({
    region: "全部",
    year: "全部",
    genre: "全部",
    sort: "popular",
  });
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const filtered = useMemo(() => {
    if (!validType) return [];
    const list = works
      .filter((item) => item.type === validType)
      .filter((item) => filters.region === "全部" || item.region === filters.region)
      .filter((item) => filters.year === "全部" || String(item.year) === filters.year)
      .filter((item) => filters.genre === "全部" || item.genres.includes(filters.genre));

    if (filters.sort === "rating") {
      list.sort((a, b) => b.rating - a.rating);
    } else if (filters.sort === "newest") {
      list.sort((a, b) => b.addedAt.localeCompare(a.addedAt));
    } else {
      list.sort((a, b) => b.popularity - a.popularity);
    }
    return list;
  }, [validType, filters]);

  if (!validType) {
    return <Navigate to="/" replace />;
  }

  const meta = categoryMeta[validType];
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleChange = (next: Partial<FilterState>) =>
    setFilters((prev) => ({ ...prev, ...next }));

  const handleReset = () =>
    setFilters({ region: "全部", year: "全部", genre: "全部", sort: "popular" });

  const pageTitle =
    validType === "movie"
      ? "电影 - 91看片高清影视资源"
      : "电视剧 - 91看片在线看";
  const pageDesc =
    validType === "movie"
      ? "浏览91看片电影频道，涵盖最新院线大片与经典高分电影，支持按地区、年份、类型筛选与热门度、评分排序。"
      : "浏览91看片电视剧频道，热播剧集与经典剧集全集在线看，支持按地区、年份、类型筛选与多维度排序。";

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <SiteHeader />

      <main className="flex-1">
        {/* 分类头部 */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-site px-4 py-6 md:px-6 md:py-8">
            <Breadcrumb items={[{ label: meta.title }]} />
            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {meta.title}
              <span className="ml-2 align-middle text-sm font-normal text-foreground-500">
                {meta.subtitle}
              </span>
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-foreground-600">
              {meta.intro}
            </p>
          </div>
        </section>

        <div className="mx-auto max-w-site px-4 py-6 md:px-6 md:py-8">
          {/* 结果统计 + 移动端筛选按钮 */}
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              共找到{" "}
              <span className="font-semibold text-primary-700">{filtered.length}</span>{" "}
              部{meta.title}
            </p>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-800 lg:hidden"
            >
              <i className="ri-filter-3-line"></i>
              筛选
            </button>
          </div>

          {/* 桌面端筛选栏 */}
          <div className="hidden lg:block">
            <FilterBar
              filters={filters}
              regionOptions={regionOptions}
              yearOptions={yearOptions}
              genreOptions={genreOptions}
              onChange={handleChange}
              onReset={handleReset}
            />
          </div>

          {/* 内容网格 */}
          {pageItems.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {pageItems.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-2-line text-3xl"></i>
              </span>
              <p className="mt-4 text-base font-medium text-foreground-800">
                暂无相关影视内容
              </p>
              <p className="mt-1 text-sm text-foreground-500">
                换个筛选条件试试，或者重置全部筛选
              </p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-5 flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}

          <Pagination current={page} total={totalPages} onChange={setPage} />
        </div>
      </main>

      {/* 移动端筛选抽屉 */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-[88%] max-w-sm overflow-y-auto bg-background-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-heading text-lg font-bold text-foreground-950">
                筛选条件
              </span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterBar
              filters={filters}
              regionOptions={regionOptions}
              yearOptions={yearOptions}
              genreOptions={genreOptions}
              onChange={handleChange}
              onReset={handleReset}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full whitespace-nowrap rounded-md bg-primary-600 py-2.5 text-sm font-medium text-background-50"
            >
              查看 {filtered.length} 个结果
            </button>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}